<script setup lang="ts">
import { computed } from 'vue'

import { DefectTypes, DefectStatusTypes } from 'Modules/types/pages/imageReview/enums'

import type { ImageType } from 'Modules/types/pages/imageReview/imageType'
import type { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import { getDefectStrokeColor } from './helperFunctions'

import CollapsibleTableRow from '@/common/components/elements/surfaces/CollapsibleTableRow.vue'

interface PropTypes {
  image: ImageType
  activeSections: Array<string>
  annotations: {
    confirmedAnnotations
    groupedAnnotationsMap
    groupedAnnotationsArray
  }
}

const props = defineProps<PropTypes>()
const confirmedAnnotations = computed(() => props.annotations.confirmedAnnotations.value)
const groupedAnnotationsMap = computed(() => props.annotations.groupedAnnotationsMap.value)
const groupedAnnotationsArray = computed(() => props.annotations.groupedAnnotationsArray.value)

const getRowClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)

  return `border-l-8 border-solid border-[${category_color}] ${annotation.isHovered ? 'bg-orange-100' : ''}`
}

const getCollapsedHeaderClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)
  const annotationsInGroup = groupedAnnotationsMap.value[annotation.annotationGroup]
  const isAnyAnnotationHovered = annotationsInGroup.some((annotation) => annotation.isHovered)
  const bgColor = isAnyAnnotationHovered ? 'bg-orange-100' : ''

  return `border-l-8 border-solid border-[${category_color}] ${bgColor} `
}

const getCollapsedRowClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)
  return `border-l-8 border-solid border-[${category_color}]`
}

const getNestedRowClass = (annotation: ImageAnnotationType): string => {
  return `${annotation.isHovered ? 'bg-orange-100' : ''}`
}
</script>

<template>
  <div class="relative h-full w-full defects-tables overflow-y-scroll" style="color: black">
    <template v-if="confirmedAnnotations.length === 0">
      <div class="h-full w-full pb-4 flex items-center justify-center text-slate-300 text-base font-medium">
        No confirmed discrepancies exist
      </div>
    </template>
    <template v-else>
      <table class="defects-table table-fixed w-full text-[14px] text-left">
        <thead>
          <tr class="bg-gray-200 border-l-8 border-l-gray-200">
            <th class="w-[1em]"></th>
            <th class="w-[5em]">ID</th>
            <th>TYPE</th>
            <th>STATUS</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="groupedAnnotationsArray.length === 0"> No defects. </template>
          <template v-for="(row, i) in groupedAnnotationsArray" v-else :key="i">
            <template v-if="Array.isArray(row)">
              <CollapsibleTableRow
                :id="`G-${row[0].annotationGroup}`"
                class="hover:cursor-pointer"
                :class="getCollapsedHeaderClass(row[0])"
              >
                <template #main>
                  <td class="border-r border-y border-gray-300">G-{{ row[0].annotationGroup }}</td>
                  <td class="border border-gray-300">{{ DefectTypes[row[0].defectType] }}</td>
                  <td class="border border-gray-300">{{ DefectStatusTypes[row[0].defectStatus] }}</td>
                </template>
                <template #details>
                  <tr :class="getCollapsedRowClass(row[0])">
                    <td colspan="4" class="no-padding">
                      <table class="w-full text-[14px] text-left">
                        <tbody>
                          <template v-for="(_row, _i) in row" :key="_i">
                            <tr
                              :id="_row.annotationUuid"
                              :class="getNestedRowClass(_row)"
                              class="hover:bg-orange-100 hover:cursor-pointer border-l-8 border-solid"
                            >
                              <td colspan="2" class="border border-gray-300 text-center">
                                {{ _row.identifier }}
                              </td>
                              <td class="border border-gray-300">{{ DefectTypes[_row.defectType] }}</td>
                              <td class="border border-gray-300">{{ DefectStatusTypes[_row.defectStatus] }}</td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
              </CollapsibleTableRow>
            </template>
            <template v-else>
              <tr
                :id="row.annotationUuid"
                class="hover:bg-orange-100 hover:cursor-pointer border-l-8 border-solid"
                :class="getRowClass(row)"
              >
                <td colspan="2" class="border border-gray-300 text-center">{{ row.identifier }}</td>
                <td class="border border-gray-300">{{ DefectTypes[row.defectType] }}</td>
                <td class="border border-gray-300">{{ DefectStatusTypes[row.defectStatus] }}</td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </template>
  </div>
</template>
